import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import EditorHeader from './header/header';
import EditorMainEventBanner from './main-event-banner/main-event-banner';
import EditorSpeakers from './speakers/speakers';
import EditorAbout from './about/about';
import EditorProducts from './products/products';
import EditorVideo from './videos/videos';
import EditorSponsors from './sponsors/sponsors';
import EditorCallout from './callout/callout';
import EditorFooter from './footer/footer';
import { EPreviewTypes, LanguagesAbbr, PageModule, PageModuleType } from '../../../../../types/working-model';
import { EventsState } from '../../../../../store/types';
import BlankSection from './blank-section';
import { TemplateClassNames } from '../../../../../types/template-layouts';
import classNames from 'classnames';
import { useParams } from 'react-router';
import { ParamsProps } from '../../../../live-event/live-event';
import EditorAgenda from './agenda-editor/agenda';
import './editor-main.scss';
import { resetScrollState } from '../../../../../store/actions/admin/event-panel';
import { getHomepageDefaultLanguage } from '../../../../../utils/get-language';
import useTranslationModules from '../../../../../i18n/useTranslationModules';
import LandingPreviewModal from './preview/eventPreview';

interface ModuleMapType {
	[key: number]: any;
}
export interface BasicLangingModuleProps {
	page_module: PageModule;
	template: TemplateClassNames;
	language: LanguagesAbbr;
	baseLanguage: LanguagesAbbr
}

const Dummy: React.FC = () => <div></div>;

export const ModuleMap: ModuleMapType = {
	[PageModuleType.main_event_banner]: EditorMainEventBanner,
	[PageModuleType.speakers]: EditorSpeakers,
	[PageModuleType.agenda]: EditorAgenda,
	[PageModuleType.about]: EditorAbout,
	[PageModuleType.videos]: EditorVideo,
	[PageModuleType.callout]: EditorCallout,
	[PageModuleType.sponsors]: EditorSponsors,
	[PageModuleType.broadcast]: Dummy,
	[PageModuleType.questions]: Dummy,
	[PageModuleType.quizzes]: Dummy,
	[PageModuleType.survey]: Dummy,
	[PageModuleType.products]: EditorProducts,
	[PageModuleType.slideshows]: Dummy,
	[PageModuleType.documents]: Dummy,
	[PageModuleType.similar_sessions]: Dummy,
	[PageModuleType.feed]: Dummy,
	[PageModuleType.embed_widget]: BlankSection,
	[PageModuleType.description]: Dummy,
	[PageModuleType.blank]: BlankSection
};


const EditorMain: React.FC = () => {
	const {
		EventPanelReducer: {
			panelOpen,
			scrollToModule,
		},
		CreateEventReducer: {
			workingEvent,
			editorSize,
			previewMode,
			loadingEvent,
			blurEditor,
		}
	} = useSelector((event: EventsState) => event);

	const dispatch = useDispatch();

	const refMap = useRef<any>({});
	const containerRef = useRef<HTMLDivElement | null>(null);
	const headerRef = useRef<HTMLDivElement | null>(null);

	const { language }: ParamsProps = useParams();

	const baseLanguage = getHomepageDefaultLanguage();

	useTranslationModules({ language: language ?? baseLanguage });

	const handleScroll = useCallback(async (type: PageModuleType) => {
		const fullRef = refMap.current[type];
		if (fullRef && containerRef?.current) {
			const headerHeight = headerRef?.current?.offsetHeight || 0;
			containerRef.current.scrollTop = fullRef.offsetTop - containerRef.current.offsetTop + headerHeight;
		}
	}, []);

	useEffect(() => {
		if (scrollToModule !== null) {
			handleScroll(scrollToModule);
		}
		// after triggering the scroll, reset state incase 
		// user clicks same module after scrolling
		setTimeout(() => dispatch(resetScrollState()), 2000);
	}, [dispatch, scrollToModule, handleScroll]);

	const modules = useMemo(() => {
		return workingEvent?.homepage?.modules?.map((module: PageModule) => {
			const Module = ModuleMap[module.type];
			// handleScroll prop because sponsors and Main Event Banner
			// have call to action buttons that can scroll to Homepage sections
			return <Module
				key={module.id}
				page_module={module}
				template={TemplateClassNames[workingEvent.template.name]}
				ref={(ref: any) => refMap.current[module.type] = ref}
				handleScroll={handleScroll}
				language={language}
				baseLanguage={baseLanguage}
			/>;
		});
	}, [workingEvent, language, baseLanguage, handleScroll]);

	return workingEvent ? (
		<div className="editor-main-border">
			<div
				className={
					classNames(
						"editor editor-main-container",
						{
							'panel-open': !!panelOpen,
							loading: loadingEvent && blurEditor,
						},
						editorSize
					)
				}
				ref={containerRef}
			>
				<EditorHeader
					ref={headerRef}
					handleScroll={handleScroll}
					template={TemplateClassNames[workingEvent.template.name]}
					language={language}
					baseLanguage={baseLanguage}
				/>
				{modules}
				<EditorFooter
					handleScroll={handleScroll}
					template={TemplateClassNames[workingEvent.template.name]}
					baseLanguage={baseLanguage}
					language={language}
				/>
				<LandingPreviewModal open={previewMode === EPreviewTypes.Landing} />
			</div>
		</div>
	) : null;
};

export default EditorMain;
